import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getUserDecks, updateDeckTitle } from "../../utils/firestore";
import DeckPreview from "../../components/DeckPreview";
import { StandardDecks } from "../../data/StandardDecks";

function CustomizeHomeView() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [decks, setDecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchDecks() {
            if (!currentUser) return;

            try {
                const fetchedDecks = await getUserDecks();
                setDecks(fetchedDecks);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching decks:", err);
                setError("Failed to load your decks");
                setLoading(false);
            }
        }

        fetchDecks();
    }, [currentUser]);

    const handleAddNewDeckClick = () => {
        navigate("/customize/intro");
    }

    // Add this function to handle editing the deck title
    const handleEditDeckTitle = async (deck) => {
        const newTitle = window.prompt("Enter a new title for your deck:", deck.title || "");
        if (newTitle !== null && newTitle.trim() !== "" && newTitle !== deck.title) {
            try {
                await updateDeckTitle(deck.id, newTitle.trim());
                setDecks(decks =>
                    decks.map(d => d.id === deck.id ? { ...d, title: newTitle.trim() } : d)
                );
            } catch (err) {
                alert("Failed to update deck title.");
            }
        }
    };

    // Support multiple standard decks (array or object)
    const standardDeckList = Array.isArray(StandardDecks)
        ? StandardDecks
        : [StandardDecks];

    // Get IDs of all standard decks
    const standardDeckIds = new Set(standardDeckList.map(d => d.id));

    // Only custom decks (not standard)
    const customDecks = decks.filter(d => !standardDeckIds.has(d.id));

    // Get IDs of unlocked decks
    const unlockedDeckIds = new Set(decks.map(d => d.id));

    if (loading) {
        return (
            <div className="mt-24 flex flex-col items-center justify-center">
                <div className="loading loading-spinner loading-lg"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-24 flex flex-col items-center justify-center">
                <div className="alert alert-error">
                    <p>{error}</p>
                </div>
                <button
                    className="btn btn-primary mt-4"
                    onClick={() => window.location.reload()}
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="pt-20 bg-white flex flex-col items-center p-4 min-h-screen">
            <h1 className="text-xl font-bold font-title text-darkgreen mb-6">Your Digital Decks</h1>

            {decks.length === 0 && (
                <div className="text-center mb-6">
                    <p className="text-gray-500">You haven't created any custom decks yet.</p>
                    <p className="text-gray-500">Create your first deck to get started!</p>
                </div>
            )}

            {/* Render standard and custom decks */}
            {(standardDeckList.length + decks.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-fit max-w-6xl mx-auto mb-8">
                    {/* Standard decks */}
                    {standardDeckList.map(deck => (
                        <div key={deck.id} className="relative group">
                            <DeckPreview
                                deck={deck}
                                isLocked={!unlockedDeckIds.has(deck.id)}
                                onClick={() => navigate(`/deck/${deck.id}`)}
                            />
                        </div>
                    ))}

                    {/* Custom decks */}
                    {customDecks.map(deck => (
                        <div key={deck.id} className="relative group">
                            <DeckPreview
                                deck={deck}
                                isLocked={false}
                                onClick={() => navigate(`/deck/${deck.id}`)}
                                onEditTitle={handleEditDeckTitle}
                            />
                        </div>
                    ))}
                </div>
            )}


            {/* Always show FAB */}
            <button
                className="fixed bottom-4 right-4 md:bottom-8 md:right-8 btn shadow-2xl hover:shadow-3xl shadow-purple-500/60 hover:shadow-purple-600/80 text-white text-lg md:text-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 flex items-center gap-2 md:gap-3 px-4 md:px-6 rounded-full border-2 border-white/30 transform hover:scale-105 transition-all duration-200 hover:animate-none"
                onClick={handleAddNewDeckClick}
                aria-label="Create custom deck"
                title="Create custom deck"
            >
                <span className="text-xl md:text-2xl">✨</span>
                <span className="font-semibold">Create Custom Deck</span>
            </button>
        </div>
    );
}

export default CustomizeHomeView;